import { useEffect, useMemo, useRef, useState } from 'react';
import { Coins, Loader2 } from 'lucide-react';
import { platformFetch } from '../../platform/http';
import type { UserData } from './types';

interface UserCreditsFormProps {
  user: UserData;
  onClose: () => void;
  onSuccess: () => void;
}

type CreditAction = 'add' | 'subtract';

function createOperationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `admin-credit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function UserCreditsForm({ user, onClose, onSuccess }: UserCreditsFormProps) {
  const [action, setAction] = useState<CreditAction>('add');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const submittingRef = useRef(false);
  const operationIdRef = useRef(createOperationId());

  useEffect(() => {
    operationIdRef.current = createOperationId();
  }, [action, amount, user.id]);

  const nextCredits = useMemo(() => {
    const parsedAmount = parseInt(amount, 10);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      return user.credits;
    }

    return action === 'add' ? user.credits + parsedAmount : user.credits - parsedAmount;
  }, [action, amount, user.credits]);

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setError('');

    const parsedAmount = parseInt(amount, 10);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setError('请输入大于 0 的积分数量');
      return;
    }

    if (action === 'subtract' && parsedAmount > user.credits) {
      setError('扣减积分不能超过当前积分');
      return;
    }

    submittingRef.current = true;
    setIsLoading(true);

    try {
      const res = await platformFetch(`/admin/users/${user.id}/credits`, {
        method: 'POST',
        body: JSON.stringify({
          action,
          amount: parsedAmount,
          operationId: operationIdRef.current,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || '积分调整失败');
        return;
      }

      operationIdRef.current = createOperationId();
      onSuccess();
    } catch {
      setError('积分调整失败，请稍后重试');
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100">
            <Coins className="h-5 w-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-blue-800">{user.email}</p>
            <p className="mt-1 text-xs text-blue-700">{user.name || '未设置昵称'}</p>
            <p className="mt-2 text-xs text-blue-700">
              当前积分：<span className="font-semibold">{user.credits.toLocaleString()}</span>
            </p>
          </div>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-light-text">
          调整方向 <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setAction('add')}
            className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
              action === 'add'
                ? 'border-green-300 bg-green-50 text-green-700'
                : 'border-light-border bg-white text-light-text hover:bg-light-hover'
            }`}
          >
            增加积分
          </button>
          <button
            type="button"
            onClick={() => setAction('subtract')}
            className={`rounded-xl border px-4 py-3 text-sm font-medium transition-colors ${
              action === 'subtract'
                ? 'border-red-300 bg-red-50 text-red-700'
                : 'border-light-border bg-white text-light-text hover:bg-light-hover'
            }`}
          >
            减少积分
          </button>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-sm font-medium text-light-text">
          积分数量 <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          min={1}
          step={1}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="请输入积分数量"
          className="w-full rounded-xl border border-light-border bg-white px-4 py-3 text-light-text placeholder-light-muted shadow-sm transition-colors focus:border-accent-blue focus:outline-none focus:ring-2 focus:ring-accent-blue/20"
          autoFocus
        />
        <p className="mt-1.5 text-xs text-light-muted">
          提交后将{action === 'add' ? '记为“赠送”' : '记为“管理员扣减”'}
        </p>
      </div>

      <div className="rounded-xl bg-light-hover p-4">
        <p className="text-sm text-light-muted">调整后积分</p>
        <p className={`mt-1 text-2xl font-bold ${nextCredits < 0 ? 'text-red-600' : 'text-light-text'}`}>
          {nextCredits.toLocaleString()}
        </p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 rounded-xl border border-light-border px-4 py-2.5 text-light-text transition-colors hover:bg-light-hover"
        >
          取消
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-accent-blue px-4 py-2.5 text-white transition-colors hover:bg-accent-blue/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              提交中...
            </>
          ) : (
            '确认调整'
          )}
        </button>
      </div>
    </form>
  );
}
